import type { ReactElement } from 'react';

import Head from 'next/head';
import Script from 'next/script';

import { GALink } from './GA';
import { Environments } from '../../../utilities/constants';
import {
  GOOGLE_TAG_MANAGER_HOST,
  getGtmConfig,
  getGtmNoScriptSrc,
  getGtmQueryParams,
  getGtmScriptBaseUrl,
  isTagGatewayEnabled,
} from '../../../utilities/constants/googleAnalytics';
import { useStaticContext } from '../../../utilities/context/static/StaticContext';
import { useAppContext } from '~/utilities/context/static/AppContext';

const getEnvironment = (): Environments | undefined =>
  Environments[process.env.ENVIRONMENT as keyof typeof Environments];

export const GTMLink = (): ReactElement => {
  const { configuration } = useStaticContext();
  const environment = getEnvironment();

  // With the tag gateway the container is same-origin, so there is nothing to preconnect to.
  if (environment && isTagGatewayEnabled(configuration.enableCLTAnalytics, environment)) {
    return <></>;
  }

  return (
    <Head>
      <link rel="preconnect" href={`https://${GOOGLE_TAG_MANAGER_HOST}`} crossOrigin="" />
    </Head>
  );
};

export const GTMNoScript = (): ReactElement => {
  const { siteType } = useAppContext();
  const environment = getEnvironment();

  if (!environment) {
    // ENVIRONMENT variable not set in env
    return <></>;
  }

  const gtmConfig = getGtmConfig(siteType, environment);
  const gtmParams = getGtmQueryParams(environment, gtmConfig);

  return (
    <noscript>
      <iframe
        title="Google Tag Manager Noscript"
        src={getGtmNoScriptSrc(gtmConfig.containerId, gtmParams)}
        height="0"
        width="0"
        style={{ display: 'none', visibility: 'hidden' }}
      />
    </noscript>
  );
};

/**
 * Google Tag Gateway (DAN-1487): when enabled, GTM is loaded from the site's own origin via
 * the Akamai-served `/clt/` measurement path instead of www.googletagmanager.com.
 * See `isTagGatewayEnabled` for the toggle + PRD-only guard.
 */
export const GTMHeadScript = (): ReactElement => {
  const { configuration } = useStaticContext();
  const { siteType } = useAppContext();
  const environment = getEnvironment();

  if (!environment) {
    // ENVIRONMENT variable not set in env
    return <></>;
  }

  const gtmConfig = getGtmConfig(siteType, environment);
  const gtmParams = getGtmQueryParams(environment, gtmConfig);
  const gtmScriptBaseUrl = getGtmScriptBaseUrl(
    isTagGatewayEnabled(configuration.enableCLTAnalytics, environment)
  );

  return (
    <>
      <GALink />
      <GTMLink />
      <Script id="gtm-head-script" strategy="afterInteractive">
        {`
        (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
        new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
        '${gtmScriptBaseUrl}?id='+i+dl+'${gtmParams}';f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','${gtmConfig.containerId}');
        `}
      </Script>
      <GTMNoScript />
    </>
  );
};
